import { apiClient } from '../client';

export interface LoginAttempt {
  _id: string;
  timestamp: Date;
  event_type: 'LOGIN_SUCCESS' | 'LOGIN_FAILURE' | 'LOGOUT' | 'PASSWORD_CHANGE' | 'PASSWORD_RESET' | 'ACCOUNT_LOCKED' | 'ACCOUNT_UNLOCKED' | 'ACCOUNT_BANNED';
  user_id?: {
    _id: string;
    email: string;
    name?: string;
  };
  email_hash: string;
  email_domain: string;
  school_id?: {
    _id: string;
    name: string;
  };
  campus_id?: string;
  auth_method: 'PASSWORD' | 'SAML' | 'OAUTH' | 'REFRESH_TOKEN' | 'API_KEY';
  user_agent: string;
  geo_location?: {
    country?: string;
    region?: string;
    city?: string;
    coordinates?: [number, number];
    timezone?: string;
  };
  session_id?: string;
  correlation_id: string;
  success: boolean;
  failure_reason?: 'INVALID_PASSWORD' | 'USER_NOT_FOUND' | 'ACCOUNT_BANNED' | 'ACCOUNT_LOCKED' | 'MFA_FAILED' | 'SESSION_EXPIRED' | 'RATE_LIMITED' | 'INVALID_TOKEN' | 'PASSWORD_BREACH';
  risk_score?: number;
  suspicious_indicators?: string[];
  response_time_ms?: number;
  metadata?: Record<string, unknown>;
}

export interface SecurityEvent extends LoginAttempt {
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface UserActivity {
  user_id: string;
  period_days: number;
  summary: {
    total_logins: number;
    failed_logins: number;
    password_changes: number;
    password_resets: number;
    unique_ips: number;
    unique_locations: number;
    risk_events: number;
  };
  activities: LoginAttempt[];
}

export interface AuditLogFilters {
  startDate: string;
  endDate: string;
  userId?: string;
  success?: boolean;
  eventType?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogResponse {
  logs: LoginAttempt[];
  total: number;
  page: number;
  totalPages: number;
  export_options: {
    bigquery: string;
    pubsub: string;
    storage: string;
    webhook: string;
  };
}

export interface ExportRequest {
  format: 'csv' | 'json';
  startDate: string;
  endDate: string;
  eventTypes?: string[];
}

class AuditService {
  /**
   * Get login audit logs
   */
  async getLoginLogs(filters: AuditLogFilters): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    
    // Add required parameters
    params.append('startDate', filters.startDate);
    params.append('endDate', filters.endDate);
    
    // Add optional parameters
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.success !== undefined) params.append('success', String(filters.success));
    if (filters.eventType) params.append('eventType', filters.eventType);
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset) params.append('offset', String(filters.offset));
    
    const response = await apiClient.get<AuditLogResponse>(`/audit/login-logs?${params.toString()}`);
    return response.data;
  }

  /**
   * Get security events
   */
  async getSecurityEvents(startDate: string, endDate: string, severity?: string): Promise<{ events: SecurityEvent[]; total: number }> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (severity) params.append('severity', severity);
    
    const response = await apiClient.get(`/audit/security-events?${params.toString()}`);
    return response.data;
  }

  /**
   * Get user activity
   */
  async getUserActivity(userId: string, days: number = 30): Promise<UserActivity> {
    const response = await apiClient.get<UserActivity>(`/audit/user-activity/${userId}?days=${days}`);
    return response.data;
  }

  /**
   * Export audit logs
   */
  async exportLogs(request: ExportRequest): Promise<Blob> {
    const response = await apiClient.post('/audit/export', request, {
      responseType: 'blob',
      headers: {
        'Accept': request.format === 'csv' ? 'text/csv' : 'application/json'
      }
    });
    return response.data;
  }

  /**
   * Download exported logs
   */
  downloadExport(blob: Blob, format: 'csv' | 'json'): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${Date.now()}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: Date | string): string {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Get event type label
   */
  getEventTypeLabel(eventType: string): string {
    const labels: Record<string, string> = {
      'LOGIN_SUCCESS': 'Successful Login',
      'LOGIN_FAILURE': 'Failed Login',
      'LOGOUT': 'Logout',
      'PASSWORD_CHANGE': 'Password Changed',
      'PASSWORD_RESET': 'Password Reset',
      'ACCOUNT_LOCKED': 'Account Locked',
      'ACCOUNT_UNLOCKED': 'Account Unlocked',
      'ACCOUNT_BANNED': 'Account Banned',
      'MFA_CHALLENGE': 'MFA Challenge',
      'MFA_SUCCESS': 'MFA Success',
      'MFA_FAILURE': 'MFA Failed'
    };
    return labels[eventType] || eventType;
  }

  /**
   * Get failure reason label
   */
  getFailureReasonLabel(reason?: string): string {
    if (!reason) return '-';
    
    const labels: Record<string, string> = {
      'INVALID_PASSWORD': 'Invalid Password',
      'USER_NOT_FOUND': 'User Not Found',
      'ACCOUNT_BANNED': 'Account Banned',
      'ACCOUNT_LOCKED': 'Account Locked',
      'MFA_FAILED': 'MFA Failed',
      'SESSION_EXPIRED': 'Session Expired',
      'RATE_LIMITED': 'Rate Limited',
      'INVALID_TOKEN': 'Invalid Token',
      'PASSWORD_BREACH': 'Password Breached'
    };
    return labels[reason] || reason;
  }

  /**
   * Get risk level
   */
  getRiskLevel(score?: number): { level: string; color: string } {
    if (!score) return { level: 'Unknown', color: 'gray' };
    
    if (score >= 75) return { level: 'Critical', color: 'red' };
    if (score >= 50) return { level: 'High', color: 'orange' };
    if (score >= 25) return { level: 'Medium', color: 'yellow' };
    return { level: 'Low', color: 'green' };
  }

}

export const auditService = new AuditService();